import { useTranslation } from 'react-i18next'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import { useTema } from '../tema/useTema'
import type { Marca, PreferenciaTema } from '../tema/contexto'
import './catalogo.css'

/**
 * Catálogo de componentes — o "equivalente ao Storybook" que UI-11 pede.
 *
 * Uma página em vez de uma ferramenta separada: assim ela é exercitada pelos
 * mesmos testes de acessibilidade, tema e largura que valem para o resto do
 * portal. Um catálogo que não passa no `axe` seria um catálogo mentindo.
 */
export function Catalogo() {
  const { t } = useTranslation()
  const { preferencia, temaEfetivo, marca, definirPreferencia, definirMarca } = useTema()

  const temas: PreferenciaTema[] = ['sistema', 'claro', 'escuro']
  const marcas: Marca[] = ['digiorgio', 'letraseversos', 'interno']

  return (
    <div className="catalogo">
      <header className="catalogo__cabecalho">
        <h1>{t('catalogo.titulo')}</h1>
        <p className="catalogo__descricao">{t('catalogo.descricao')}</p>

        <div className="catalogo__controles">
          <fieldset>
            <legend>{t('tema.titulo')}</legend>
            {temas.map((op) => (
              <label key={op}>
                <input
                  type="radio"
                  name="tema"
                  value={op}
                  checked={preferencia === op}
                  onChange={() => definirPreferencia(op)}
                />
                {t(`tema.${op}`)}
              </label>
            ))}
            <span className="catalogo__efetivo" data-teste="tema-efetivo">
              {t(`tema.${temaEfetivo}`)}
            </span>
          </fieldset>

          <fieldset>
            <legend>{t('marca.titulo')}</legend>
            {marcas.map((op) => (
              <label key={op}>
                <input
                  type="radio"
                  name="marca"
                  value={op}
                  checked={marca === op}
                  onChange={() => definirMarca(op)}
                />
                {t(`marca.${op}`)}
              </label>
            ))}
          </fieldset>
        </div>
      </header>

      <div className="catalogo__grade">
        <Cartao titulo={t('catalogo.estados')}>
          <div className="catalogo__estados">
            <Estado tipo="carregando" />
            <Estado tipo="vazio" />
            <Estado
              tipo="erro"
              acao={<Botao variante="secundario">{t('comum.tentarDeNovo')}</Botao>}
            />
            <Estado tipo="semPermissao" />
          </div>
        </Cartao>

        <Cartao titulo={t('catalogo.botoes')}>
          <div className="catalogo__linha">
            <Botao variante="primario">{t('acao.primaria')}</Botao>
            <Botao variante="secundario">{t('acao.secundaria')}</Botao>
            <Botao variante="perigo">{t('acao.perigosa')}</Botao>
            <Botao variante="discreto">{t('comum.cancelar')}</Botao>
            <Botao variante="primario" carregando>
              {t('comum.salvar')}
            </Botao>
            <Botao variante="secundario" disabled>
              {t('acao.desabilitada')}
            </Botao>
          </div>
        </Cartao>

        <Cartao titulo={t('catalogo.campos')}>
          <div className="catalogo__coluna">
            <Campo rotulo={t('campo.rotulo')} ajuda={t('campo.ajuda')} obrigatorio />
            <Campo rotulo={t('campo.rotulo')} erro={t('campo.erro')} obrigatorio />
          </div>
        </Cartao>

        <Cartao titulo={t('catalogo.avisos')}>
          <div className="catalogo__coluna">
            <Aviso tom="info">{t('comum.vazioDescricao')}</Aviso>
            <Aviso tom="sucesso">{t('comum.salvar')}</Aviso>
            <Aviso tom="alerta">{t('comum.semPermissaoDescricao')}</Aviso>
            <Aviso tom="perigo" titulo={t('comum.erro')}>
              {t('comum.erroDescricao')}
            </Aviso>
          </div>
        </Cartao>

        <Cartao titulo={t('catalogo.tipografia')}>
          <dl className="catalogo__formatos">
            <dt>{t('catalogo.exemploNumero')}</dt>
            <dd>{formatar.numero(1234567.891)}</dd>
            <dt>{t('catalogo.exemploMoeda')}</dt>
            <dd>{formatar.moeda('1234.5')}</dd>
            <dt>{t('catalogo.exemploData')}</dt>
            <dd>{formatar.dataHora('2026-08-04T13:45:00')}</dd>
          </dl>
          <p className="catalogo__plural">
            {t('catalogo.exemploPlural', { count: 1 })} ·{' '}
            {t('catalogo.exemploPlural', { count: 7 })}
          </p>
        </Cartao>
      </div>
    </div>
  )
}
